import {
    ApprovalScopeEnum,
    ApprovalStatusEnum,
    Employee,
    Expense,
    ExpenseKindEnum,
    ExpenseRequestTypeEnum,
    ExpenseStatusEnum,
    Hospitality,
    InboxInvoice,
    InvoiceSplit,
    Mileage,
    PerDiem,
    SplitTypeEnum,
    TransactionData,
    TripFolder,
    Vendor,
    VendorTypeEnum,
} from "@finway-group/shared/lib/models"
import { ExpensePaymentFlowInformationEnum } from "@finway-group/shared/lib/models/expense/expensePaymentFlowInformation.enum"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { getApprovalProcess } from "@finway-group/shared/lib/utils/approvalProcess.utils"
import * as Sentry from "@sentry/react"
import { Button, Form, Modal } from "antd"
import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { useModal } from "Shared/context/modal.context"

import { ExpenseForm } from "Components/forms/expenseForm/ExpenseForm"
import { usePurchaseOrdersSelectedForExpense } from "Features/pages/purchaseOrders/hooks/usePurchaseOrdersSelected"
import { FINWAY_ADMIN_EMAIL, NOT_SET_VALUE } from "Shared/config/consts"
import { useCompany } from "Shared/hooks/company.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useExpense } from "Shared/hooks/expense.hooks"
import { useFormTouchedHandler } from "Shared/hooks/form.hooks"
import { usePerDiemDestinations } from "Shared/hooks/perDiemDestination.hooks"
import { useRolesMap } from "Shared/hooks/role.hooks"
import { useTaxes } from "Shared/hooks/tax.hooks"
import { PurchaseOrderReference } from "Shared/models/purchaseOrderReference"
import { fetchVendor } from "Shared/queries/vendors/vendor.queries"
import { ApprovalProcessService, EmployeeService, ExpenseHttpService, ExpenseService, NotificationService, UserService } from "Shared/services"
import DialogService from "Shared/services/dialog.service"
import { NotificationDurationSec, NotificationTypeEnum } from "Shared/services/notification.service"
import { createExpense, createPreApprovedSubscription, updateExpense } from "Shared/store/actions/expense/expenseActions"
import { deleteManyInboxInvoices } from "Shared/store/actions/inboxInvoice/inboxInvoiceActions"
import { getNextApproverId } from "Shared/utils/approvalProcess.utils"
import { getAbsentApprovers, getAvailableReplacementsForAbsentApprover } from "Shared/utils/employee.utils"
import {
    addInvoiceCommentsIfNeeded,
    createLinkToRequestFunction,
    deleteUnnecessaryExpenseFormData,
    doTotalAndInlineAmountsMismatch,
    isExpenseWithoutInvoice,
    isFolderExpense,
    isMileageExpense,
    isMileageOrPerDiem,
    isPerDiemExpense,
    syncTotalExpenseAmountsToItems,
} from "Shared/utils/expense.utils"

import { FormControlsArrowKeysFix } from "Components/form/formControlsArrowKeysFix"
import { isPerDiemTripNotReimbursable } from "Shared/utils/perDiem.utils"
import ExpenseFormContext from "./expenseCreateForm.context"
import { PurchaseOrderLinkModal } from "./PurchaseOrderLink/PurchaseOrderLinkModal"
import ReplacementForAbsentApproverModal from "./replacementForAbsentApprover.modal"

interface ExpenseCreateFormInterface {
    kind?: ExpenseKindEnum
    type: ExpenseRequestTypeEnum
    isShowing: boolean
    isNew: boolean
    isPreApprovedSubscription?: boolean
    transactionData?: TransactionData
    isReimbursement?: boolean
    onExpenseCreated?: (expense: Expense) => void
    onCancel: () => void
    inboxInvoices?: Array<InboxInvoice>
    inboxOcrSource?: InboxInvoice

    /** Used when we don't want to load the expense from the redux, e.g. editing a sub-expense */
    expenseOverride?: Expense | PerDiem | Mileage | Hospitality

    /** Used when we're making an sub-expense for a folder */
    folder?: TripFolder

    /** Enables/Disables request types selection. By default all are enabled. */
    reimbursementRequestsEnabled?: { standard: boolean; mileage: boolean; hospitality: boolean; perDiem: boolean }

    /** Use when directly adding an expense to a PO from the PO details page, omit otherwise. */
    preSelectedPurchaseOrder?: PurchaseOrderReference
}

export const ExpenseCreateFormModal = ({
    type,
    kind = ExpenseKindEnum.ONE_TIME_EXPENSE,
    isShowing,
    isNew = true,
    isPreApprovedSubscription = false,
    isReimbursement = false,
    transactionData,
    onExpenseCreated,
    onCancel,
    inboxInvoices,
    inboxOcrSource,
    reimbursementRequestsEnabled,
    expenseOverride,
    folder,
    preSelectedPurchaseOrder = undefined,
}: ExpenseCreateFormInterface) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const loggedInUser = UserService.getLoggedInEmployeeProfile()
    const employees = useEmployees({ excludeDeleted: true, includeAutoApprover: true })
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isUploading, setIsUploading] = useState(false)
    const [isBuildingApprovalProcesses, setIsBuildingApprovalProcesses] = useState(false)
    const [isReplacementForAbsentApproverShowing, setIsReplacementForAbsentApproverShowing] = useState(false)
    const [absentApprovers, setAbsentApprovers] = useState<Array<Employee>>([])
    const [availableReplacementsForAbsentApprover, setAvailableReplacementsForAbsentApprover] = useState<Array<Employee>>([])
    const [checkIfApproverInAbsence, setCheckIfApproverInAbsence] = useState(true)
    const [replacementForAbsentApprover, setReplacementForAbsentApprover] = useState("")

    const { showModal } = useModal()
    const rolesMap = useRolesMap({ excludeDeleted: true })
    const { destinations: perDiemDestinations } = usePerDiemDestinations()
    const history = useHistory()

    const [expenseForm] = Form.useForm()
    const taxes = useTaxes()
    const { isFormTouched, handleTouchForm, handleResetTouchForm } = useFormTouchedHandler()

    // Separating this to its on reduxExpense because we have to maintain the amount of hook calls between renders.
    const reduxExpense = useExpense()
    const expenseDetails = expenseOverride ?? reduxExpense

    const { selectedPurchaseOrders, setSelectedPurchaseOrders } = usePurchaseOrdersSelectedForExpense({ purchaseOrder: preSelectedPurchaseOrder, isNew, expense: expenseDetails })

    const company = useCompany()

    const isInvoiceRequest = type === ExpenseRequestTypeEnum.INVOICE
    const isSubscriptionRequest = type === ExpenseRequestTypeEnum.SUBSCRIPTION
    const isPurchaseRequest = type === ExpenseRequestTypeEnum.PURCHASE
    isReimbursement = type === ExpenseRequestTypeEnum.REIMBURSEMENT
    const isTripFolder = type === ExpenseRequestTypeEnum.TRIP_FOLDER

    const isSubmittingRef = useRef(false)

    const setIsSubmitting = (isSubmitting: boolean) => {
        setIsLoading(isSubmitting)
        isSubmittingRef.current = isSubmitting
    }

    const onSubmit = (
        expense: ((Expense & { replacementForAbsentApprover?: string }) | Mileage | Hospitality | PerDiem | TripFolder) & { initializeNotStartedApprovalProcess?: boolean },
        extraData?: { originalApproverId?: string },
    ) => {
        if (isSubmittingRef.current) {
            Sentry.captureMessage(`Duplicate submission prevented: expenseNumber=${expense?.expenseNumber}`)
            return
        }

        setIsSubmitting(true)

        return onSubmitInternal(expense, extraData).finally(() => {
            setIsSubmitting(false)
        })
    }

    const onSubmitInternal = async (
        expense: ((Expense & { replacementForAbsentApprover?: string }) | Mileage | Hospitality | PerDiem | TripFolder) & { initializeNotStartedApprovalProcess?: boolean },
        extraData?: { originalApproverId?: string },
    ) => {
        const approvalScope = isPurchaseRequest ? ApprovalScopeEnum.EXPENSE_PURCHASE : ApprovalScopeEnum.EXPENSE_INVOICE
        const approvalProcess = getApprovalProcess(approvalScope, expense.approvalProcesses)
        if (checkIfApproverInAbsence && !replacementForAbsentApprover) {
            const absentApprovers = approvalProcess ? getAbsentApprovers(approvalProcess, employees) : []
            if (absentApprovers.length > 0) {
                // substitutedBy only in /employees/:id API
                const absentApproversWithSubstituteDetails = await Promise.all(absentApprovers.map((a) => EmployeeService.fetchEmployeeById(a.id)))
                const absentApproversWithoutSubstitute = absentApproversWithSubstituteDetails.filter((a) => a !== null).filter((a) => !a.substitutedBy)
                if (absentApproversWithoutSubstitute.length > 0) {
                    setAvailableReplacementsForAbsentApprover(getAvailableReplacementsForAbsentApprover(expense, employees, rolesMap))
                    setAbsentApprovers(absentApproversWithoutSubstitute)
                    setIsReplacementForAbsentApproverShowing(true)
                    return
                }
            }
        }
        const autoApprover = employees.find(({ email }) => email === FINWAY_ADMIN_EMAIL)

        ;(expense as any).replacementForAbsentApprover = replacementForAbsentApprover

        const hasDuplicate = await ExpenseService.checkHasDuplicate(expense, expense.invoiceNumber, { ignoreIds: inboxInvoices?.map(({ id }) => id) })
        if (hasDuplicate) {
            return
        }

        // confirm reporting only status
        if (
            expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY &&
            !(await DialogService.confirmPaymentFlowInformationReportingOnlyWarningOnCreation())
        ) {
            return
        }

        // confirm mismatching amounts
        if (doTotalAndInlineAmountsMismatch(expense, taxes, isNew) && (await DialogService.confirmAmountAdjustment())) {
            const { totalNetPrice, totalTaxPrice, totalGrossPrice, taxRate } = syncTotalExpenseAmountsToItems(expense.splits)
            const updatedExpense = new Expense({ ...expense, totalNetPrice, totalTaxPrice, totalGrossPrice, taxRate })
            const approvalProcesses = await ApprovalProcessService.getApprovalProcessesAfterExpenseUpdate(updatedExpense, company, {
                previousExpense: expense,
                awaitConfirmation: true,
            })
            if (approvalProcesses) {
                updatedExpense.approvalProcesses = approvalProcesses
            }
            expense = updatedExpense
        }

        if (isPerDiemExpense(expense)) {
            if (isPerDiemTripNotReimbursable(expense, perDiemDestinations)) {
                await DialogService.confirmPerDiemLessThan8Hours()
                return
            }
        }

        const vendor = expense.vendorType === VendorTypeEnum.VENDOR ? await fetchVendor(expense.vendor?._id) : undefined

        if (company.gobdCompliance?.enforceGobdCompliantInvoice) {
            const { isCompliant } = await ExpenseHttpService.checkForExpenseGobdCompliance(expense.id, expense)
            if (isCompliant) {
                const isConfirmed = await DialogService.confirmExpenseGobdCompliantInvoiceFlagging({
                    expense,
                    history,
                    selectedVendor: vendor,
                })

                if (!isConfirmed) {
                    return
                }
            }
        }

        // if cost center 2 or expense account is "not set" remove value because api "" (empty string) is not a valid ID
        // and undefined select value is not supported by antd :/
        if (expense.costCenter2 === NOT_SET_VALUE) {
            expense.costCenter2 = undefined
        }
        if (expense.expenseAccount?._id === NOT_SET_VALUE || !expense.expenseAccount?._id) {
            expense.expenseAccount = undefined
        }
        if (expense.taxRate?._id === NOT_SET_VALUE) {
            expense.taxRate = undefined
        }

        // Clean split's cc2 or expense account from NOT_SET_VALUE
        for (const split of expense.splits) {
            if (split.costCenter2 === NOT_SET_VALUE) {
                split.costCenter2 = undefined
            }
            if (split.expenseAccount?._id === NOT_SET_VALUE || !split.expenseAccount?._id) {
                split.expenseAccount = undefined
            }
        }

        // confirm auto approver
        const nextApproverId = getNextApproverId(expense.approvalProcesses)
        const isFirstStepTheAutoApprover = nextApproverId && nextApproverId === autoApprover?.id // auto approver can only be selected as first approver and will be alone in the step.
        const willThereBeAtLeastAnApproverOtherThanTheAutoApprover = (() => {
            if (!isFirstStepTheAutoApprover) {
                return true
            }
            if (!approvalProcess?.steps) {
                return false
            }
            return approvalProcess?.steps.slice(1)?.some((step) => step.status !== ApprovalStatusEnum.SKIPPED)
        })()

        if (
            !willThereBeAtLeastAnApproverOtherThanTheAutoApprover &&
            company?.showAutoApproverWarning &&
            (extraData?.originalApproverId !== autoApprover?.id || extraData?.originalApproverId !== nextApproverId || isNew) &&
            expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY &&
            !(await DialogService.confirmAutoApprover())
        ) {
            return
        }

        if (expense.splitType === SplitTypeEnum.SPLIT && expense.splits.length === 1) {
            const split = expense.splits[0] as InvoiceSplit
            split.taxRate = expense.taxRate
            split.netPrice = expense.totalNetPrice
            split.grossPrice = expense.totalGrossPrice
            split.taxPrice = expense.totalTaxPrice
        }

        if (isMileageOrPerDiem(expense)) {
            // Mileage and per diem will always have the requester as the vendor
            expense.creditorUser = { _id: expense.requestedBy?._id }
            expense.vendorType = VendorTypeEnum.USER
            // For mileage requests, the invoiceDate will be datePurchased.
            // For per diem requests, the invoiceDate will be datePurchased, derived from the last date of the trip.
            expense.invoiceDate = expense.datePurchased

            // Parse the route if it is a string to submit it in the correct format
            if (isMileageExpense(expense) && expense.route && typeof expense.route === "string") {
                expense.route = JSON.parse(expense.route)
            }
        }

        if (isExpenseWithoutInvoice(expense)) {
            expense.invoices = []
        }

        if (isNew && folder && expense.kind !== ExpenseKindEnum.TRIP_FOLDER) {
            // If created under a folder, then assign the folder id to the expense
            expense.folderId = folder?.id
        }

        const approvalProcesses =
            isNew || expense.folderId
                ? expense.approvalProcesses
                : await ApprovalProcessService.getMergedApprovalProcesses(expenseDetails.approvalProcesses, expense.approvalProcesses, {
                      awaitConfirmation: !isPreApprovedSubscription, // Since in pre approved subscriptions the approval processes are always in status "approved" we don't need to confirm the change.
                      prioritizeApproved: isPreApprovedSubscription,
                  })
        if (!approvalProcesses) {
            return
        }
        expense.approvalProcesses = approvalProcesses

        // When expense request is created we will initiate the approval process automatically.
        // If the expense is being updated, we will not and user will have to click on "submit" to initialize the approval process on their own.
        // @issue https://levaroio.atlassian.net/browse/CUS-259
        expense.initializeNotStartedApprovalProcess = isNew

        // If expense is a sub-expense of a folder...
        if (expense.folderId && folder?.approvalProcesses) {
            const folderApprovalProcess = getApprovalProcess(ApprovalScopeEnum.EXPENSE_INVOICE, folder.approvalProcesses)

            // Do not initialize it if its folder is not initialized.
            if (folderApprovalProcess?.status === ApprovalStatusEnum.NOT_STARTED) {
                expense.initializeNotStartedApprovalProcess = false
            }

            // Check if we need to update the folder approval process after an amount change. (Since children change the total amount of folders)
            const isFolderAmountChangeApproved = await ApprovalProcessService.confirmFolderAmountChange(expense.totalGrossPrice - expenseDetails.totalGrossPrice, folder, company)
            if (!isFolderAmountChangeApproved) {
                return
            }
        }

        if (isFolderExpense(expense)) {
            if (isNew) {
                expense.status = ExpenseStatusEnum.INVOICE_PENDING
            }
            // TODO: Remove this and calculate totalTax and netPrice in the evaluateFolderExec fn.
            expense.totalNetPrice = 0
            expense.totalTaxPrice = 0
            expense.splits = []
            // Folders cannot be initiated via the expense form. Should always be initiated via the "submit" button.
            expense.initializeNotStartedApprovalProcess = false
        }

        await onExpenseAction(expense, vendor)
    }
    const handleHide = () => {
        setIsSubmitting(false)
        handleResetTouchForm()
        onCancel()
    }

    const onCreateExpense = async (expense: Expense | Mileage | Hospitality | PerDiem, vendor: Vendor | undefined): Promise<Expense | undefined> => {
        const shouldAddExpenseAccountFromVendorRule = !expense.expenseAccount && expense.vendorType === VendorTypeEnum.VENDOR
        if (shouldAddExpenseAccountFromVendorRule) {
            expense.expenseAccount = vendor?.rule?.expenseAccount ? { _id: vendor?.rule?.expenseAccount } : undefined
        }

        const createAction = isPreApprovedSubscription ? createPreApprovedSubscription : createExpense

        return createAction(expense)(dispatch)
            .then((newExpense: Expense) => {
                if (isFolderExpense(expense)) {
                    NotificationService.send(
                        NotificationTypeEnum.SUCCESS,
                        t("notification:trip_folder.created.title"),
                        t("notification:trip_folder.created.message"),
                        NotificationDurationSec.REQUEST_CREATION,
                        undefined,
                        {
                            onClick: createLinkToRequestFunction(newExpense, history),
                            text: t("notification:trip_folder.created.link"),
                        },
                    )
                } else if (
                    expense.isSubscription() &&
                    expense.isSuccessfullyProcessed() &&
                    expense.isInitialSubscription() &&
                    expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD
                ) {
                    NotificationService.send(
                        NotificationTypeEnum.SUCCESS,
                        t("notification:request.created_with_card_processing.title"),
                        t("notification:request.created_with_card_processing.message"),
                        20,
                        undefined,
                        {
                            onClick: createLinkToRequestFunction(newExpense, history),
                            text: t("notification:request.created.link"),
                        },
                    )
                } else {
                    NotificationService.send(
                        NotificationTypeEnum.SUCCESS,
                        t("notification:request.created.title"),
                        t("notification:request.created.message"),
                        NotificationDurationSec.REQUEST_CREATION,
                        undefined,
                        {
                            onClick: createLinkToRequestFunction(newExpense, history),
                            text: t("notification:request.created.link"),
                        },
                    )
                }

                if (onExpenseCreated) {
                    onExpenseCreated(newExpense)
                }

                if (inboxInvoices?.length) {
                    deleteManyInboxInvoices(inboxInvoices.map((invoice) => invoice.id))(dispatch)
                }
                setReplacementForAbsentApprover("")
                setCheckIfApproverInAbsence(true)

                return newExpense
            })
            .catch((err) => {
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:request.create.title"))
                return undefined
            })
    }

    const onUpdateExpense = async (expense: Expense): Promise<Expense | undefined> => {
        addInvoiceCommentsIfNeeded(expense, expenseDetails, loggedInUser)

        return updateExpense(
            expense.id,
            expense,
        )(dispatch)
            .then((updatedExpense) => {
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:request.updated.title"), t("notification:request.updated.message"))

                if (inboxInvoices?.length) {
                    deleteManyInboxInvoices(inboxInvoices.map((invoice) => invoice.id))(dispatch)
                }
                setReplacementForAbsentApprover("")
                setCheckIfApproverInAbsence(true)

                return updatedExpense
            })
            .catch((err) => {
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:request.edit.title"))
                return undefined
            })
    }

    const onExpenseAction = async (expense: Expense | Mileage | Hospitality | PerDiem, vendor: Vendor | undefined) => {
        setIsLoading(true)
        // delete helper form fields
        const sanitizedExpense = deleteUnnecessaryExpenseFormData(expense)
        let updatedExpense: Expense | undefined
        if (isNew) {
            updatedExpense = await onCreateExpense(sanitizedExpense, vendor)
        } else {
            updatedExpense = await onUpdateExpense(sanitizedExpense)
        }

        if (!updatedExpense) {
            // undefined means the expense was not created or updated
            return
        }

        // Show purchase order link modal if needed, otherwise just close
        const shouldShowPurchaseOrderLinkModal = !!selectedPurchaseOrders?.length
        if (shouldShowPurchaseOrderLinkModal) {
            showModal(PurchaseOrderLinkModal, true, {
                isShowing: true,
                expense: updatedExpense,
                selectedPurchaseOrders,
                onCancel: handleHide,
            })
            return
        }
        handleHide()
    }

    const getModalTitle = (): string => {
        if (isPreApprovedSubscription) {
            return isNew ? t("action:subscription.create") : t("action:subscription.edit")
        }

        if (isReimbursement) {
            if (isNew) {
                return t("action:reimbursement_request.create")
            }
            if (isMileageExpense(expenseDetails) || isPerDiemExpense(expenseDetails)) {
                return t("action:reimbursement_request.edit")
            }
            return t("action:invoice_request.edit")
        }

        if (isInvoiceRequest) {
            if (!isNew) {
                return t("action:invoice_request.edit")
            }
            if (preSelectedPurchaseOrder) {
                return t("action:invoice_request.create_for_po", { poNumber: preSelectedPurchaseOrder.purchaseOrderNumber })
            }
            return t("action:invoice_request.create")
        }

        if (isSubscriptionRequest) {
            return isNew ? t("action:subscription_request.create") : t("action:subscription_request.edit")
        }

        if (isPurchaseRequest) {
            return isNew ? t("action:purchase_request.create") : t("action:purchase_request.edit")
        }

        if (isTripFolder) {
            return isNew ? t("action:trip_folder.create") : t("action:trip_folder.edit")
        }

        return isNew ? t("action:request.create") : t("action:request.edit")
    }

    const getActionButtonTitle = () => {
        if (isPreApprovedSubscription) {
            return isNew ? t("action:subscription.create") : t("action:subscription.save")
        }

        if (isTripFolder) {
            return isNew ? t("action:trip_folder.submit") : t("action:trip_folder.save")
        }

        if (selectedPurchaseOrders?.length) {
            return t("action:po_link.link_items")
        }

        return isNew ? t("action:request.submit") : t("action:request.save")
    }

    const handleHideOnCloseOrCancel = async () => {
        if (isFormTouched.current) {
            const isConfirmed = await DialogService.confirmUnsavedChanges()
            if (!isConfirmed) {
                return
            }
        }

        handleHide()
    }

    return (
        <FormControlsArrowKeysFix>
            <Modal
                destroyOnClose={true}
                afterClose={() => expenseForm.resetFields()}
                open={isShowing}
                maskClosable={false}
                title={getModalTitle()}
                onCancel={handleHideOnCloseOrCancel}
                closable={true}
                keyboard={true}
                className={isInvoiceRequest || isReimbursement || isTripFolder ? "ant-modal--full" : "ant-modal--large"}
                footer={[
                    <Button id="expenseCreateModalCancel" className="btn-default" key="back" onClick={handleHideOnCloseOrCancel}>
                        {t("action:cancel")}
                    </Button>,
                    <Button
                        data-testid="expenseCreateModalSubmit"
                        key="submit"
                        type="primary"
                        loading={isLoading}
                        disabled={isUploading || isBuildingApprovalProcesses}
                        onClick={() => expenseForm.submit()}
                    >
                        {getActionButtonTitle()}
                    </Button>,
                ]}
            >
                <ExpenseFormContext
                    isNew={isNew}
                    type={type}
                    expenseForm={expenseForm}
                    kind={kind}
                    isReimbursement={isReimbursement}
                    isPreApprovedSubscription={isPreApprovedSubscription}
                    transactionData={transactionData}
                    isUploading={isUploading}
                    setIsUploading={setIsUploading}
                    inboxInvoices={inboxInvoices}
                    reimbursementRequestsEnabled={reimbursementRequestsEnabled}
                    expenseOverride={expenseDetails}
                    folder={folder}
                    isBuildingApprovalProcesses={isBuildingApprovalProcesses}
                    setIsBuildingApprovalProcesses={setIsBuildingApprovalProcesses}
                    selectedPurchaseOrders={selectedPurchaseOrders}
                    setSelectedPurchaseOrders={setSelectedPurchaseOrders}
                    preSelectedPurchaseOrder={preSelectedPurchaseOrder}
                >
                    <ExpenseForm inboxOcrSource={inboxOcrSource} onSubmit={onSubmit} onFormValuesChange={handleTouchForm} />
                </ExpenseFormContext>
                {isReplacementForAbsentApproverShowing && (
                    <ReplacementForAbsentApproverModal
                        id="replacementApprover"
                        availableReplacementsForAbsentApprover={availableReplacementsForAbsentApprover}
                        absentApprovers={absentApprovers}
                        isVisible={isReplacementForAbsentApproverShowing}
                        closeModal={() => setIsReplacementForAbsentApproverShowing(false)}
                        onSubmit={() => expenseForm.submit()}
                        setCheckIfApproverInAbsence={setCheckIfApproverInAbsence}
                        setReplacementForAbsentApprover={setReplacementForAbsentApprover}
                        isPurchaseRequest={isPurchaseRequest}
                    />
                )}
            </Modal>
        </FormControlsArrowKeysFix>
    )
}

// TODO get rid of default export
export default ExpenseCreateFormModal
